import { DiagLogLevel, SpanStatusCode, trace } from "@opentelemetry/api";
import { SeverityNumber, logs } from "@opentelemetry/api-logs";
import { OTLPMetricExporter } from "@opentelemetry/exporter-metrics-otlp-http";
import { MeterProvider, PeriodicExportingMetricReader } from "@opentelemetry/sdk-metrics";

//#region src/logs.ts
/**
* Helper to emit a log to OpenTelemetry
* This can be called manually from your code when you want to send logs to OTEL
*/
function emitOtelLog(level, context, message, attributes) {
	const otelLogger = logs.getLogger(context);
	const getSeverity = (level) => {
		switch (level.toLowerCase()) {
			case "debug": return SeverityNumber.DEBUG;
			case "verbose": return SeverityNumber.TRACE;
			case "log":
			case "info": return SeverityNumber.INFO;
			case "warn": return SeverityNumber.WARN;
			case "error": return SeverityNumber.ERROR;
			default: return SeverityNumber.INFO;
		}
	};
	const spanContext = trace.getActiveSpan()?.spanContext();
	const traceId = spanContext?.traceId;
	const spanId = spanContext?.spanId;
	otelLogger.emit({
		severityNumber: getSeverity(level),
		severityText: level.toUpperCase(),
		body: message,
		attributes: {
			...attributes,
			trace_id: traceId,
			span_id: spanId
		}
	});
}

//#endregion
//#region src/metrics.ts
const meter = new MeterProvider({ readers: [new PeriodicExportingMetricReader({
	exporter: new OTLPMetricExporter({}),
	exportIntervalMillis: 250
})] }).getMeter("@tg-search/observability");
/**
* WebSocket send fail total
*/
const wsSendFailTotal = meter.createCounter("ws.send.fail.total", { description: "Total number of failed WebSocket sends from server to client" });
/**
* WebSocket connections active gauge
*/
const wsConnectionsActive = meter.createUpDownCounter("ws.connections.active", { description: "Number of active WebSocket connections" });
/**
* Core events in total
*/
const coreEventsInTotal = meter.createCounter("core.events.in.total", { description: "Total number of events sent from client to core" });
/**
* Core messages processed total
*/
const coreMessagesProcessedTotal = meter.createCounter("core.messages.processed.total", { description: "Total number of messages processed by core message resolver" });
/**
* Core message batches processed total
*/
const coreMessageBatchesProcessedTotal = meter.createCounter("core.message.batches.processed.total", { description: "Total number of message batches processed by core message resolver" });
/**
* Core message batch duration histogram
*/
const coreMessageBatchDurationMs = meter.createHistogram("core.message.batch.duration.ms", {
	description: "Duration of message processing batches in milliseconds",
	unit: "ms"
});
/**
* Core resolver duration histogram
*/
const coreResolverDurationMs = meter.createHistogram("core.resolver.duration.ms", {
	description: "Duration of individual message resolvers in milliseconds",
	unit: "ms"
});
/**
* Core takeout messages downloaded total
*/
const coreTakeoutDownloadedTotal = meter.createCounter("core.takeout.downloaded.total", { description: "Total number of messages downloaded from Telegram via takeout" });
/**
* Create OpenTelemetry counter from CoreCounter
*/
function createOtelCounter(otelCounter) {
	return { inc(labels, value) {
		otelCounter.add(value ?? 1, labels);
	} };
}
/**
* Create OpenTelemetry histogram from CoreHistogram
*/
function createOtelHistogram(otelHistogram) {
	return { observe(labels, value) {
		otelHistogram.record(value, labels);
	} };
}
/**
* Core metrics
*/
const coreMetrics = {
	messagesProcessed: createOtelCounter(coreMessagesProcessedTotal),
	messageBatchDuration: createOtelHistogram(coreMessageBatchDurationMs),
	resolverDuration: createOtelHistogram(coreResolverDurationMs),
	takeoutDownloadTotal: createOtelCounter(coreTakeoutDownloadedTotal)
};

//#endregion
//#region src/traces.ts
async function withSpan(spanName, fn) {
	return trace.getTracer("@tg-search/observability").startActiveSpan(spanName, async (span) => {
		try {
			return await fn();
		} catch (error) {
			span.recordException(error);
			span.setStatus({ code: SpanStatusCode.ERROR });
			throw error;
		} finally {
			span.end();
		}
	});
}

//#endregion
export { DiagLogLevel, coreEventsInTotal, coreMessageBatchDurationMs, coreMessageBatchesProcessedTotal, coreMessagesProcessedTotal, coreMetrics, coreResolverDurationMs, coreTakeoutDownloadedTotal, emitOtelLog, withSpan, wsConnectionsActive, wsSendFailTotal };
//# sourceMappingURL=index.mjs.map